/**
 * The assistant panel's class constants.
 *
 * They live here rather than inline in the parts so the panel's visual vocabulary
 * — one radius, one border weight, one type scale — is stated once and can be read
 * in one place. Every colour is a design token: the assistant shows a model's
 * output, and an output that picked up a raw palette colour would be the only
 * surface in the console that dark mode cannot correct.
 */

/* --------------------------------------------------------------------------- */
/* THE PANEL                                                                    */
/* --------------------------------------------------------------------------- */

/** The panel's body: a column that owns the whole height. */
export const assistantBodyClasses = 'flex min-h-0 flex-1 flex-col';

/** The row above the conversation: the history toggle and the new-conversation action. */
export const assistantToolbarClasses =
  'flex items-center justify-between gap-2 border-b border-border px-3 py-2';

/** The composer, pinned below the conversation. */
export const assistantComposerClasses =
  'flex flex-col gap-2 border-t border-border bg-surface-raised p-3';

/** The row the composer's controls sit in. */
export const assistantComposerRowClasses = 'flex items-end justify-between gap-2';

/** The history section, above the conversation. */
export const assistantHistoryClasses =
  'max-h-48 shrink-0 overflow-y-auto border-b border-border bg-surface-sunken p-3';

/** A notice above the conversation — a failure the person needs to read. */
export const assistantNoticeClasses =
  'border-b border-border bg-critical-soft px-3 py-2 text-sm text-content';

/* --------------------------------------------------------------------------- */
/* THE CONVERSATION                                                             */
/* --------------------------------------------------------------------------- */

/** The message log. `role="log"` is announced politely as turns arrive. */
export const assistantMessageListClasses =
  'flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-3 py-3';

/** One turn. */
export const assistantTurnClasses = 'flex flex-col gap-1';

/** The person's own message. */
export const assistantUserTurnClasses =
  'self-end max-w-[85%] rounded-control rounded-br-sm bg-accent px-3 py-2 text-md text-accent-content';

/** The agent's answer. */
export const assistantAgentTurnClasses =
  'self-start max-w-[95%] rounded-control rounded-bl-sm border border-border bg-surface px-3 py-2 text-md text-content';

/** The name above a turn. */
export const assistantTurnAuthorClasses = 'text-2xs uppercase tracking-wide text-content-subtle';

/** The empty state inside the log. */
export const assistantLogEmptyClasses = 'text-sm text-content-muted';

/* --------------------------------------------------------------------------- */
/* THE SUGGESTIONS                                                              */
/* --------------------------------------------------------------------------- */

/** The prefilled suggestions for the page. */
export const assistantSuggestionListClasses =
  'flex flex-col gap-1.5 border-b border-border px-3 py-3';

/** One suggestion. */
export const assistantSuggestionClasses =
  'rounded-control border border-border bg-surface px-2.5 py-1.5 text-left text-sm text-content transition-colors hover:border-accent hover:text-accent';

/* --------------------------------------------------------------------------- */
/* THE HISTORY                                                                  */
/* --------------------------------------------------------------------------- */

/** One conversation in the history. */
export const assistantThreadClasses =
  'flex w-full flex-col gap-0.5 rounded-control border border-transparent px-2 py-1.5 text-left transition-colors hover:border-border hover:bg-surface';

/** The selected conversation. */
export const assistantThreadSelectedClasses = 'border-border bg-surface';

/** The conversation's title. */
export const assistantThreadTitleClasses = 'truncate text-sm font-medium text-content';

/** The conversation's turn count and age. */
export const assistantThreadMetaClasses = 'text-2xs text-content-subtle';

/* --------------------------------------------------------------------------- */
/* THE DRAFT                                                                    */
/* --------------------------------------------------------------------------- */

/** The draft's review block. */
export const assistantDraftClasses =
  'mt-2 flex flex-col gap-2 rounded-control border border-caution bg-caution-soft p-2.5';

/** The draft's heading. */
export const assistantDraftHeadingClasses = 'text-sm font-semibold text-content';

/** The server's review sentence, shown verbatim. */
export const assistantDraftReviewClasses = 'text-sm text-content';

/** The proposed values. */
export const assistantDraftFieldClasses =
  'flex items-baseline justify-between gap-3 border-b border-border py-1 last:border-b-0';

/** A field's name. */
export const assistantDraftFieldNameClasses = 'text-sm text-content-muted';

/** A field's proposed value. */
export const assistantDraftFieldValueClasses =
  'max-w-[60%] break-words text-right text-sm font-medium text-content';

/** The draft's actions. */
export const assistantDraftActionsClasses = 'flex flex-wrap items-center gap-2';

/* --------------------------------------------------------------------------- */
/* THE MARKDOWN                                                                 */
/* --------------------------------------------------------------------------- */

/** The rendered answer. */
export const markdownClasses = 'flex flex-col gap-2 text-md leading-relaxed text-content';

/** A paragraph. */
export const markdownParagraphClasses = 'text-md text-content';

/** A heading, at any of the three levels. */
export const markdownHeadingClasses = 'font-semibold text-content';

/** A list, bullet or numbered. */
export const markdownListClasses = 'list-outside pl-4 text-md text-content';

/** One list item. */
export const markdownListItemClasses = 'mt-0.5';

/** A fenced code block. */
export const markdownCodeBlockClasses =
  'overflow-x-auto rounded-control border border-border bg-surface-sunken p-2.5';

/** The code inside a fenced block. */
export const markdownCodeTextClasses = 'font-mono text-sm whitespace-pre text-content';

/** An inline code span. */
export const markdownInlineCodeClasses =
  'rounded-control bg-surface-sunken px-1 py-0.5 font-mono text-sm text-content';

/** Bold text. */
export const markdownStrongClasses = 'font-semibold text-content';

/** Italic text. */
export const markdownEmphasisClasses = 'italic';

/** A link the console is willing to make clickable. */
export const markdownLinkClasses = 'text-accent underline underline-offset-2';

/** The URL of a link the console refused to make clickable. */
export const markdownRefusedLinkClasses = 'text-content-muted';

/** A horizontal rule. */
export const markdownRuleClasses = 'my-1 border-t border-border';

/** The wrapper that lets a wide table scroll instead of overflowing the panel. */
export const markdownTableWrapperClasses = 'overflow-x-auto rounded-control border border-border';

/** The table itself. */
export const markdownTableClasses = 'w-full border-collapse text-sm';

/** A header cell. */
export const markdownTableHeaderCellClasses =
  'border-b border-border bg-surface-sunken px-2 py-1 text-left font-semibold text-content';

/** A body cell. */
export const markdownTableCellClasses = 'border-b border-border px-2 py-1 align-top text-content';
